import { PlatformSection } from '@/common/components';
import { useNewTranslations } from '@/common/translations';
import type { Tuple5 } from '@/common/types';
import { KnockoutFinalPhase, KnockoutNational, TeamsTable } from '@/ui/components';
import { TwoJerseysWithStats } from '../../components';
import { KnockoutType, OverviewType } from '../../constants';
import { usePrematchData } from '../../hooks';
import { mapGroupTableTeamsToTableRowData } from '../../utils/mapGroupTableTeamsToTableRowData';

export default function OverviewSection() {
  const { teams, statistics } = usePrematchData();
  const translate = useNewTranslations();

  const sectionName = translate('PreMatchStats.Overview');
  const statsLabels: Tuple5<string> = [
    translate('PreMatchStats.TableColumnPlayedShort'),
    translate('PreMatchStats.TableColumnVictoryShort'),
    translate('PreMatchStats.TableColumnDrawShort'),
    translate('PreMatchStats.TableColumnLossShort'),
    translate('PreMatchStats.TableColumnPointsShort'),
  ];

  if (!statistics?.overview) {
    return null;
  }

  const { type, groupTable, home, away } = statistics.overview;

  if (type === OverviewType.KNOCKOUT) {
    const { knockout } = statistics.overview;

    if (!knockout) {
      return null;
    }

    if (knockout.type === KnockoutType.FINAL_PHASE) {
      const homeMissing = knockout.home.missingPlayers;
      const awayMissing = knockout.away.missingPlayers;

      if (homeMissing.length === 0 && awayMissing.length === 0) {
        return null;
      }

      const hasGoalscorers =
        knockout.home.lastMatchGoalscorers.length > 0 ||
        knockout.away.lastMatchGoalscorers.length > 0;

      return (
        <PlatformSection name={sectionName}>
          <KnockoutFinalPhase
            home={{
              jerseySrc: teams.home.jersey,
              missingPlayers: homeMissing,
              goalscorers: knockout.home.lastMatchGoalscorers,
            }}
            away={{
              jerseySrc: teams.away.jersey,
              missingPlayers: awayMissing,
              goalscorers: knockout.away.lastMatchGoalscorers,
            }}
            showGoalscorers={hasGoalscorers}
            goalscorerLastMatchLabel={translate('PreMatchStats.Knockout.GoalscorerLastMatch')}
            missingPlayersLabel={translate('PreMatchStats.Knockout.MissingPlayers')}
          />
        </PlatformSection>
      );
    }

    if (knockout.type === KnockoutType.NATIONAL_TEAM) {
      return (
        <PlatformSection name={sectionName}>
          <KnockoutNational
            home={{
              flagSrc: knockout.home.flag,
              ranking: knockout.home.rank ?? 0,
              missingPlayers: knockout.home.missingPlayers,
            }}
            away={{
              flagSrc: knockout.away.flag,
              ranking: knockout.away.rank ?? 0,
              missingPlayers: knockout.away.missingPlayers,
            }}
            rankingLabel={translate('PreMatchStats.Knockout.FifaRanking')}
            missingPlayersLabel={translate('PreMatchStats.Knockout.MissingPlayers')}
          />
        </PlatformSection>
      );
    }

    return null;
  }

  if (type === OverviewType.GROUPS_TABLES) {
    if (!groupTable) {
      return null;
    }

    const rowData = mapGroupTableTeamsToTableRowData(groupTable.teams);

    return (
      <PlatformSection name={sectionName}>
        <div className="flex flex-col gap-2">
          <span className="font-robotoMedium font-medium text-[12px] leading-[16px] text-pmw-label-primary-light dark:text-pmw-label-primary-dark">
            {groupTable.groupName}
          </span>
          <TeamsTable rowData={rowData} />
        </div>
      </PlatformSection>
    );
  }

  if (!home || !away) {
    return null;
  }

  return (
    <PlatformSection name={sectionName}>
      <TwoJerseysWithStats
        homeConfig={{
          jerseySrc: teams.home.jersey,
          size: 82,
          withOverlay: true,
          teamNumber: home.no,
          leagueName: home.leagueName,
          statsLabels,
          statsValues: [home.playedMatches, home.wins, home.draws, home.losses, home.points],
        }}
        awayConfig={{
          jerseySrc: teams.away.jersey,
          size: 82,
          withOverlay: true,
          teamNumber: away.no,
          leagueName: away.leagueName,
          statsLabels,
          statsValues: [away.playedMatches, away.wins, away.draws, away.losses, away.points],
        }}
      />
    </PlatformSection>
  );
}
